/**
 * @fileoverview Componente responsável por destacar atendimentos ou agentes com atrasos de resposta.
 */

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Collapse,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Popover,
  TextField,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import ptBR from "date-fns/locale/pt-BR";
import { ChevronDown, ChevronUp, Eye, Calendar } from "lucide-react";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      minWidth: "600px",
      maxWidth: "800px",
      background: theme.palette.type === 'dark'
        ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.95) 0%, rgba(22, 22, 24, 0.95) 100%)'
        : '#ffffff',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
      borderRadius: '24px',
      boxShadow: theme.palette.type === 'dark'
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
        : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
      [theme.breakpoints.down('sm')]: {
        minWidth: 'unset',
        maxWidth: 'unset',
        borderRadius: 0,
      },
    },
  },
  dialogTitle: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(91, 124, 255, 0.1) 0%, rgba(157, 124, 255, 0.1) 100%)'
      : 'linear-gradient(135deg, rgba(61, 92, 253, 0.05) 0%, rgba(138, 35, 135, 0.05) 100%)',
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.12)',
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: "column",
      alignItems: "stretch",
      gap: theme.spacing(1),
    },
  },
  titleTextGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  dialogContent: {
    background: 'transparent',
  },
  dialogActions: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(15, 15, 17, 0.5) 0%, rgba(20, 20, 22, 0.5) 100%)'
      : 'transparent',
    borderTop: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.12)',
  },
  userItem: {
    background: theme.palette.type === "light"
      ? "#f8f9fa"
      : 'linear-gradient(135deg, rgba(22, 22, 24, 0.6) 0%, rgba(40, 40, 43, 0.6) 100%)',
    marginBottom: theme.spacing(1),
    borderRadius: '14px',
    border: theme.palette.type === "light"
      ? "1px solid #e0e0e0"
      : '1px solid rgba(91, 124, 255, 0.15)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid #d0d0d0',
    },
  },
  ticketItem: {
    background: theme.palette.type === "light"
      ? "#ffffff"
      : 'linear-gradient(135deg, rgba(25, 25, 27, 0.5) 0%, rgba(35, 35, 37, 0.5) 100%)',
    marginBottom: theme.spacing(0.5),
    borderRadius: '10px',
    paddingLeft: theme.spacing(4),
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.08)' : 'none',
  },
  countChip: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
  },
  timeChip: {
    marginLeft: theme.spacing(1),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  emptyContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
  },
  unassignedItem: {
    background: theme.palette.type === "light"
      ? "#fff3cd"
      : 'linear-gradient(135deg, rgba(61, 53, 25, 0.8) 0%, rgba(71, 63, 35, 0.8) 100%)',
    border: theme.palette.type === "light"
      ? "1px solid #ffc107"
      : '1px solid rgba(255, 193, 7, 0.3)',
    '&:hover': {
      border: theme.palette.type === 'dark' ? '1px solid rgba(255, 193, 7, 0.5)' : '1px solid #ffc107',
    },
  },
  unassignedText: {
    color: theme.palette.type === "light" ? "#856404" : "#ffc107",
  },
  filterRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      width: "100%",
    },
  },
  rangeInput: {
    minWidth: 180,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
      flex: 1,
    },
    "& .MuiInputBase-root": {
      borderRadius: 999,
      background: theme.palette.type === 'dark'
        ? 'rgba(17, 17, 19, 0.6)'
        : 'rgba(255, 255, 255, 0.9)',
      boxShadow: theme.palette.type === 'dark'
        ? '0 6px 18px rgba(0,0,0,0.25)'
        : '0 2px 6px rgba(15, 23, 42, 0.08)',
      paddingRight: theme.spacing(0.5),
      transition: 'border-color 160ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(148, 163, 184, 0.18)'
        : 'rgba(15, 23, 42, 0.10)',
    },
    "& .MuiOutlinedInput-root:hover fieldset": {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(148, 163, 184, 0.32)'
        : 'rgba(15, 23, 42, 0.18)',
    },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: theme.palette.type === 'dark'
        ? 'rgba(91, 124, 255, 0.55)'
        : 'rgba(59, 130, 246, 0.45)',
      borderWidth: 1,
    },
    "& input": {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: theme.palette.type === 'light' ? '#0f172a' : '#e5e7eb',
      cursor: 'pointer',
    },
  },
  rangePopover: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    width: "fit-content",
    maxWidth: "95vw",
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: theme.spacing(1),
    },
  },
  rangePicker: {
    "& .rdrCalendarWrapper": {
      background: theme.palette.type === "dark" ? "rgba(15, 15, 17, 0.98)" : "#ffffff",
      color: theme.palette.type === "dark" ? "#e5e7eb" : "#0f172a",
      borderRadius: 16,
      border: theme.palette.type === "dark"
        ? "1px solid rgba(91, 124, 255, 0.18)"
        : "1px solid #e2e8f0",
      boxShadow: theme.palette.type === "dark"
        ? "0 18px 40px rgba(0,0,0,0.45)"
        : "0 12px 28px rgba(15, 23, 42, 0.12)",
      overflow: "hidden",
    },
    "& .rdrMonthAndYearWrapper": {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1),
      background: theme.palette.type === "dark"
        ? "linear-gradient(135deg, rgba(91, 124, 255, 0.12), rgba(29, 78, 216, 0.08))"
        : "linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(14, 165, 233, 0.06))",
      borderBottom: theme.palette.type === "dark"
        ? "1px solid rgba(91, 124, 255, 0.15)"
        : "1px solid #e2e8f0"
    },
    "& .rdrMonthAndYearPickers select": {
      color: theme.palette.type === "dark" ? "#e5e7eb" : "#0f172a",
      background: "transparent",
      fontWeight: 600
    },
    "& .rdrWeekDay": {
      color: theme.palette.type === "dark" ? "#94a3b8" : "#64748b",
      fontWeight: 600,
      textTransform: "uppercase",
      fontSize: "0.68rem"
    },
    "& .rdrDayNumber span": {
      color: theme.palette.type === "dark" ? "#e5e7eb" : "#0f172a"
    },
    "& .rdrDayDisabled": {
      background: "transparent"
    },
    "& .rdrDayDisabled .rdrDayNumber span": {
      color: theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.35)" : "#cbd5e1"
    },
    "& .rdrDayPassive .rdrDayNumber span": {
      color: theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.45)" : "#94a3b8"
    },
    "& .rdrDayToday .rdrDayNumber span:after": {
      background: theme.palette.type === "dark" ? "#60a5fa" : "#3b82f6"
    },
    "& .rdrDayHovered": {
      outline: "none"
    },
    "& .rdrDayStartPreview, & .rdrDayInPreview, & .rdrDayEndPreview": {
      borderColor: theme.palette.type === "dark" ? "rgba(96, 165, 250, 0.7)" : "rgba(59, 130, 246, 0.7)"
    },
    "& .rdrInRange, & .rdrStartEdge, & .rdrEndEdge": {
      color: "#3b82f6"
    },
    "& .rdrStartEdge, & .rdrEndEdge": {
      borderRadius: 10
    },
    "& .rdrInRange": {
      background: theme.palette.type === "dark" ? "rgba(59, 130, 246, 0.18)" : "rgba(59, 130, 246, 0.12)"
    },
    "& .rdrDefinedRangesWrapper": {
      display: "none"
    }
  },
  rangeChip: {
    alignSelf: "flex-start",
    fontSize: '0.7rem',
    fontWeight: 600,
    background: theme.palette.type === 'dark'
      ? 'rgba(91, 124, 255, 0.15)'
      : 'rgba(59, 130, 246, 0.12)',
    color: theme.palette.type === 'dark' ? '#c7d2fe' : '#1e40af',
    border: theme.palette.type === 'dark'
      ? '1px solid rgba(91, 124, 255, 0.3)'
      : '1px solid rgba(59, 130, 246, 0.25)',
  },
  filterActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1)
  },
}));

const NoResponseOffendersModal = ({ open, onClose, forceLast24h = false, groupName }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [offenders, setOffenders] = useState([]);
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedUnassigned, setExpandedUnassigned] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [draftRange, setDraftRange] = useState({
    startDate: moment().toDate(),
    endDate: moment().toDate(),
    key: "selection"
  });

  useEffect(() => {
    if (open) {
      fetchOffenders();
    }
  }, [open, dateFrom, dateTo, forceLast24h, groupName]);

  useEffect(() => {
    if (open && forceLast24h) {
      setDateFrom(null);
      setDateTo(null);
      setFilterAnchorEl(null);
    }
  }, [open, forceLast24h]);

  const formatRangeLabel = (startValue, endValue) => {
    if (forceLast24h) return "Últimas 24h";
    if (!startValue || !endValue) return "Todos";
    const start = moment(startValue);
    const end = moment(endValue);
    const today = moment().format("YYYY-MM-DD");
    if (start.format("YYYY-MM-DD") === today && end.format("YYYY-MM-DD") === today) {
      return "Hoje";
    }
    if (start.isSame(end, "day")) {
      return start.format("D MMM");
    }
    return `de ${start.format("D MMM")} a ${end.format("D MMM")}`;
  };

  const getRangeLabel = () => formatRangeLabel(dateFrom, dateTo);
  const getDraftRangeLabel = () => formatRangeLabel(draftRange.startDate, draftRange.endDate);

  const fetchOffenders = async () => {
    setLoading(true);
    try {
      const params = {};
      if (!forceLast24h) {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      }
      if (groupName) params.groupName = groupName;

      const { data } = await api.get("/dashboard/noResponseOffenders", { params });
      setOffenders(data.data);
      setUnassignedTickets(data.unassignedTickets || []);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    }
    setLoading(false);
  };

  const handleOpenFilter = (event) => {
    if (forceLast24h) return;
    setDraftRange({
      startDate: dateFrom ? moment(dateFrom).toDate() : moment().toDate(),
      endDate: dateTo ? moment(dateTo).toDate() : moment().toDate(),
      key: "selection"
    });
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilter = () => {
    const start = moment(draftRange.startDate);
    const end = moment(draftRange.endDate);
    setDateFrom(start.format("YYYY-MM-DD"));
    setDateTo(end.format("YYYY-MM-DD"));
    setFilterAnchorEl(null);
  };

  const handleClearFilter = () => {
    setDateFrom(null);
    setDateTo(null);
    setFilterAnchorEl(null);
  };

  const handleToggleUser = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleViewTicket = (ticketId) => {
    history.push(`/tickets/${ticketId}?from=noresponse`);
    onClose();
  };

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  return (
    <Dialog open={open} onClose={onClose} className={classes.dialog} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleTextGroup}>
          <Typography variant="h6" component="div" style={isMobile ? { fontSize: '1rem' } : undefined}>
            Usuários com Tickets Sem Resposta (+10min)
          </Typography>
          <Typography variant="body2" color="textSecondary" style={isMobile ? { fontSize: '0.75rem' } : undefined}>
            Atendentes com maior número de tickets aguardando resposta
          </Typography>
        </Box>
        {!forceLast24h && (
          <Box className={classes.filterRow}>
            <TextField
              className={classes.rangeInput}
              variant="outlined"
              size="small"
              value={getRangeLabel()}
              onClick={handleOpenFilter}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <Calendar size={16} strokeWidth={1.75} color="#94a3b8" />
                ),
              }}
            />
          </Box>
        )}
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : offenders.length === 0 && unassignedTickets.length === 0 ? (
          <Box className={classes.emptyContainer}>
            <Typography variant="h6" color="textSecondary">
              🎉 Nenhum ticket sem resposta!
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Todos os atendentes estão em dia com os atendimentos.
            </Typography>
          </Box>
        ) : (
          <List>
            {/* Seção de Tickets Sem Usuário Atribuído */}
            {unassignedTickets.length > 0 && (
              <Box>
                <ListItem
                  button
                  onClick={() => setExpandedUnassigned(!expandedUnassigned)}
                  className={`${classes.userItem} ${classes.unassignedItem}`}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="subtitle1" component="span" className={classes.unassignedText}>
                          ⚠️ Sem Usuário Atribuído
                        </Typography>
                        <Chip
                          label={`${unassignedTickets.length} ${unassignedTickets.length === 1 ? 'ticket' : 'tickets'}`}
                          style={{ backgroundColor: "#ffc107", color: "#000", marginLeft: 8 }}
                          size="small"
                          className={classes.countChip}
                        />
                      </Box>
                    }
                    secondary={
                      <span className={classes.unassignedText}>
                        {`${unassignedTickets.length} ticket${unassignedTickets.length !== 1 ? 's' : ''} aguardando atribuição há +10min`}
                      </span>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => setExpandedUnassigned(!expandedUnassigned)}>
                      {expandedUnassigned ? <ChevronUp size={20} strokeWidth={1.75} /> : <ChevronDown size={20} strokeWidth={1.75} />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>

                <Collapse in={expandedUnassigned} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {unassignedTickets.map((ticket) => (
                      <ListItem key={ticket.id} className={classes.ticketItem}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2">
                                <strong>{ticket.contactName}</strong> - {ticket.contactNumber}
                              </Typography>
                              <Chip
                                label={formatTime(ticket.minutesWithoutResponse)}
                                style={{ backgroundColor: "#ff9800", color: "#fff" }}
                                size="small"
                                className={classes.timeChip}
                              />
                            </Box>
                          }
                          secondary={`Ticket #${ticket.id} - Última mensagem: ${new Date(
                            ticket.lastMessageAt
                          ).toLocaleString("pt-BR")}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            color="primary"
                            onClick={() => handleViewTicket(ticket.id)}
                            title="Ver ticket"
                          >
                            <Eye size={18} strokeWidth={1.75} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
                <Divider style={{ margin: "8px 0" }} />
              </Box>
            )}

            {/* Seção de Usuários com Tickets Sem Resposta */}
            {offenders.map((offender) => (
              <Box key={offender.userId}>
                <ListItem button onClick={() => handleToggleUser(offender.userId)} className={classes.userItem}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center">
                        <Typography variant="subtitle1" component="span">
                          {offender.userName}
                        </Typography>
                        <Chip
                          label={`${offender.ticketCount} ${offender.ticketCount === 1 ? 'ticket' : 'tickets'}`}
                          color="secondary"
                          size="small"
                          className={classes.countChip}
                          style={{ marginLeft: 8 }}
                        />
                      </Box>
                    }
                    secondary={`${offender.ticketCount} atendimento${offender.ticketCount !== 1 ? 's' : ''} sem resposta`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleToggleUser(offender.userId)}>
                      {expandedUser === offender.userId ? <ChevronUp size={20} strokeWidth={1.75} /> : <ChevronDown size={20} strokeWidth={1.75} />}
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>

                <Collapse in={expandedUser === offender.userId} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {offender.tickets.map((ticket) => (
                      <ListItem key={ticket.id} className={classes.ticketItem}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography variant="body2">
                                <strong>{ticket.contactName}</strong> - {ticket.contactNumber}
                              </Typography>
                              <Chip
                                label={formatTime(ticket.minutesWithoutResponse)}
                                color="primary"
                                size="small"
                                className={classes.timeChip}
                              />
                            </Box>
                          }
                          secondary={`Ticket #${ticket.id} - Última mensagem: ${new Date(
                            ticket.lastMessageAt
                          ).toLocaleString("pt-BR")}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            color="primary"
                            onClick={() => handleViewTicket(ticket.id)}
                            title="Ver ticket"
                          >
                            <Eye size={18} strokeWidth={1.75} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
                <Divider style={{ margin: "8px 0" }} />
              </Box>
            ))}
          </List>
        )}
      </DialogContent>
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box className={classes.rangePopover}>
          <Typography style={{ fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>
            Selecione o período
          </Typography>
          <div className={classes.rangePicker}>
            <DateRangePicker
              ranges={[draftRange]}
              onChange={(item) => setDraftRange(item.selection)}
              moveRangeOnFirstSelection={false}
              showSelectionPreview
              months={isMobile ? 1 : 2}
              direction={isMobile ? "vertical" : "horizontal"}
              locale={ptBR}
              rangeColors={["#3b82f6"]}
              weekdayDisplayFormat="EEEEE"
              monthDisplayFormat="MMMM yyyy"
              weekStartsOn={0}
              showMonthArrow
              showDateDisplay={false}
            />
          </div>
          <Chip label={getDraftRangeLabel()} className={classes.rangeChip} />
          <Box className={classes.filterActions}>
            <Button size="small" onClick={handleClearFilter}>
              Todos
            </Button>
            <Button size="small" onClick={handleCloseFilter}>
              Cancelar
            </Button>
            <Button size="small" color="primary" variant="contained" onClick={handleApplyFilter}>
              Aplicar
            </Button>
          </Box>
        </Box>
      </Popover>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoResponseOffendersModal;
